import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import Autocomplete from "../../../components/Places/PlacesAutocomplete";
import {
  MapPinIcon,
  UserIcon,
  IdentificationIcon,
} from "@heroicons/react/24/outline";
import FormInput from "../../../components/controls/FormInput";

const TrouverRepresentantPage = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const [searchType, setSearchType] = useState("address");
  const [inputValue, setInputValue] = useState("");
  const [coordinates, setCoordinates] = useState(null);
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);

  const handleSearch = async () => {
    const params = new URLSearchParams();

    if (searchType === "address" && coordinates) {
      params.append("lat", coordinates.lat);
      params.append("lng", coordinates.lng);
    } else if (searchType === "name") {
      params.append("name", inputValue.trim());
    } else if (searchType === "code") {
      params.append("code", inputValue.trim());
    }

    setLoading(true);
    try {
      const res = await fetch(
        `${
          process.env.REACT_APP_API_BASE_URL
        }/api/Representative/search?${params.toString()}`
      );
      const data = await res.json();
      setResults(data);
    } catch (err) {
      console.error("Erreur recherche :", err);
      setResults([]);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="bg-gray-50 min-h-screen py-10 px-4 text-primary">
      <div className="max-w-xl mx-auto bg-white shadow-md rounded-lg p-6">
        <h1 className="text-2xl font-bold text-center ">
          {t("representatives.findTitle")}
        </h1>
        <p className="font-medium text-center mb-6">
          {t("representatives.selectSearchType")}
        </p>

        <div className="mb-4">
          <div className="flex justify-center gap-4 text-sm">
            <label className="flex items-center gap-1">
              <input
                type="radio"
                name="searchType"
                value="address"
                checked={searchType === "address"}
                onChange={() => {
                  setSearchType("address");
                  setInputValue("");
                  setCoordinates(null);
                }}
              />
              {t("representatives.fields.address")}
            </label>
            <label className="flex items-center gap-1">
              <input
                type="radio"
                name="searchType"
                value="name"
                checked={searchType === "name"}
                onChange={() => {
                  setSearchType("name");
                  setCoordinates(null);
                  setInputValue("");
                }}
              />
              {t("representatives.fields.name")}
            </label>
            <label className="flex items-center gap-1 hidden">
              <input
                type="radio"
                name="searchType"
                value="code"
                checked={searchType === "code"}
                onChange={() => {
                  setSearchType("code");
                  setCoordinates(null);
                }}
              />
              {t("representatives.fields.code")}
            </label>
          </div>
        </div>

        {/* Search input (dynamique selon critère) */}
        <div className="mb-4">
          <label className="block mb-1 font-medium">
            {searchType === "address"
              ? t("representatives.fields.address")
              : searchType === "name"
              ? t("representatives.fields.name")
              : t("representatives.fields.code")}
          </label>

          {searchType === "address" ? (
            <Autocomplete
              placeholder={t("representatives.placeholders.address")}
              value={inputValue}
              onSelect={(addr, coords) => {
                setInputValue(addr);
                setCoordinates(coords);
              }}
            />
          ) : (
            <FormInput
              value={inputValue}
              onChange={(name, value) => setInputValue(value)}
              showRequiredOrOptional={false}
            ></FormInput>
          )}
        </div>

        <div className="text-center">
          <button
            type="submit"
            onClick={handleSearch}
            className="bg-papayaTitle text-white font-semibold px-6 py-2 rounded-full hover:bg-yellow-400 transition"
          >
            {t("representatives.buttons.search")}
          </button>
        </div>
      </div>

      {/* Résultats */}
      <div className="max-w-4xl mx-auto mt-10">
        {loading && (
          <p className="text-center">{t("representatives.loading")}</p>
        )}
        {!loading && results.length === 0 && (
          <p className="text-center text-gray-500 mt-4">
            {t("representatives.noResults")}
          </p>
        )}
        <div className="grid gap-6 mt-6">
          {results.map((rep) => {
            const hasValidLocation =
              rep.location?.coordinates &&
              (rep.location.coordinates[0] !== 0 ||
                rep.location.coordinates[1] !== 0);

            return (
              <div
                key={rep.id}
                className="border p-4 rounded-md bg-white shadow cursor-pointer hover:shadow-md"
                onClick={() => navigate(`/representant/${rep.code}`)}
              >
                <h2 className="text-lg font-semibold text-papayaTitle mb-1">
                  {rep.firstName} {rep.lastName}
                </h2>

                {rep.city && hasValidLocation && (
                  <div className="flex items-center text-sm text-gray-700">
                    <MapPinIcon className="w-5 h-5 mr-2 text-papayaTitle" />
                    {rep.city}
                  </div>
                )}

                {rep.email && (
                  <div className="flex items-center text-sm text-gray-700">
                    <UserIcon className="w-5 h-5 mr-2 text-papayaTitle" />
                    {rep.email}
                  </div>
                )}

                {rep.code && (
                  <div className="flex items-center text-sm text-gray-700">
                    <IdentificationIcon className="w-5 h-5 mr-2 text-papayaTitle" />
                    {rep.code}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};

export default TrouverRepresentantPage;
